import {
  Plus, Paperclip, Trash2, Check, Ban, CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { VoiceDictateButton } from "@/components/journal/VoiceDictateButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChildAssignPicker } from "@/components/expenses/ChildAssignPicker";
import { deriveReceiptStatus } from "@/components/ReceiptStatusBadge";
import { money, currencySymbol } from "@/lib/format";
import { API, getToken } from "@/lib/api";

/**
 * ExpenseDialogs
 *
 * Bundles every modal dialog the Expenses page renders into a single
 * presentational component: Settle-all, Record-payment (debtor),
 * Confirm-payment-received (payer), Record-receipt (payer one-sided),
 * Attach-receipt, Edit, and Void.
 *
 * Extracted from pages/Expenses.jsx in the v77.99.19 Phase 3 frontend
 * decomposition pass (slice 2). All test IDs, copy, validation, and
 * styling are preserved verbatim.
 *
 * Props are grouped to keep the prop surface manageable. Each dialog
 * is a controlled fragment — its open state, form state, busy state,
 * and submit/cancel handlers come from the parent.
 */
export function ExpenseDialogs({
  // shared context
  currency, childList, items,
  CATS, catLabel, SPLIT_OPTIONS_NON_DEFAULT, PM_OPTIONS, PM_LABEL,
  eligibleParentsFor, nameOf,
  // settle-all
  settleOpen, setSettleOpen, outstandingCount, summary,
  paymentMethod, setPaymentMethod, paymentRef, setPaymentRef,
  settleBusy, settleAll,
  // record-payment (debtor)
  payFor, setPayFor, payForm, setPayForm, submitPay,
  // confirm payment (payer)
  confirmFor, setConfirmFor, disputeReason, setDisputeReason,
  confirmBusy, doDispute, doConfirm,
  // record-receipt (payer one-sided)
  receiptFor, setReceiptFor, receiptForm, setReceiptForm,
  receiptBusy, submitReceipt,
  // attach receipt
  attachFor, setAttachFor, attachFile, setAttachFile,
  attachBusy, submitAttach,
  // edit
  editFor, setEditFor, editForm, setEditForm,
  editBusy, submitEdit, openAttach, setNotRequired, detachReceipt,
  // void
  voidFor, setVoidFor, voidReason, setVoidReason, submitVoid,
}) {
  return (
    <>
      {/* Settle-all dialog */}
      <Dialog open={settleOpen} onOpenChange={setSettleOpen}>
        <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3]" data-testid="settle-dialog">
          <DialogHeader><DialogTitle className="font-heading">Mark all as settled?</DialogTitle></DialogHeader>
          <div className="text-sm text-[#2E3A3F] space-y-3">
            <p>This will close out <span className="font-semibold">{outstandingCount}</span> open expense{outstandingCount === 1 ? "" : "s"} (including partials) and log a settlement record on each.</p>
            {summary?.settlements?.length > 0 && (
              <div className="border border-[#D9E1E3] bg-[#EEF3F4] rounded-sm p-3" data-testid="settle-dialog-transfers">
                <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">
                  {summary.settlements.length === 1 ? "Net settlement" : "Net settlements"}
                </div>
                <ul className="mt-1 space-y-1">
                  {summary.settlements.map((s, i) => (
                    <li key={`${s.from_id}-${s.to_id}-${i}`} className="font-mono-data">
                      <span className="font-semibold">{s.from_name}</span> pays <span className="font-semibold">{s.to_name}</span>{" "}
                      <span className="text-[#C0291D] font-semibold">{money(s.amount, currency)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div>
              <Label>Payment method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger data-testid="settle-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PM_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reference <span className="text-[#6F828A] font-normal">(optional)</span></Label>
              <Input value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} placeholder="Transaction ID, bank ref…" data-testid="settle-reference-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSettleOpen(false)} disabled={settleBusy} data-testid="settle-cancel-button">Cancel</Button>
            <Button onClick={settleAll} disabled={settleBusy} className="bg-[#1F9FA4] hover:bg-[#178085]" data-testid="settle-confirm-button">{settleBusy ? "Settling…" : "Confirm settlement"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record payment (debtor's perspective — two-sided: co-parent must confirm) */}
      <Dialog open={!!payFor} onOpenChange={(v) => !v && setPayFor(null)}>
        <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3]" data-testid="payment-dialog">
          <DialogHeader><DialogTitle className="font-heading">Record payment you made</DialogTitle></DialogHeader>
          {payFor && (() => {
            // v77.99.114 — Credit-adjusted remaining helper (must
            // match the backend safeguard in /expenses/{id}/payments).
            const _allocs = payFor.credit_allocations || [];
            const _allocSum = _allocs.reduce((s, a) => s + (a.amount || 0), 0);
            const _grossRemaining = payFor.breakdown?.outstanding_to_payer || 0;
            const _creditAdjRemaining = Math.max(0, _grossRemaining - _allocSum);
            const _typed = parseFloat(payForm.amount) || 0;
            const _overLimit = _typed > _creditAdjRemaining + 0.005;
            return (
            <div className="space-y-3">
              <p className="text-sm text-[#6F828A]">
                Log a payment you made to <span className="text-[#2E3A3F] font-medium">{nameOf(payFor.paid_by)}</span> toward the <span className="text-[#2E3A3F] font-medium">{payFor.title}</span> expense. They still need to confirm they received it before the balance updates.
                Remaining to pay back: <span className="font-mono-data text-[#C0291D] font-semibold">{money(_creditAdjRemaining, currency)}</span>
              </p>
              {_allocSum > 0.005 && (
                <div className="text-xs text-[#0F5658] bg-[#D6F0EF] border border-[#A7D8DA] rounded-sm p-2" data-testid="payment-credit-note">
                  <strong className="font-mono-data">{money(_allocSum, currency)}</strong> is already covered by a payment credit on this expense (gross outstanding {money(_grossRemaining, currency)}). Logging more than the remaining {money(_creditAdjRemaining, currency)} would double-credit the same money.
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Amount you paid ({currencySymbol(currency)})</Label>
                  <Input
                    type="number"
                    step="0.01"
                    max={_creditAdjRemaining}
                    value={payForm.amount}
                    onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                    className={_overLimit ? "border-[#A93A2F] focus-visible:ring-[#A93A2F]" : ""}
                    data-testid="payment-amount-input"
                  />
                  {_overLimit && (
                    <div className="text-[11px] text-[#A93A2F] mt-1" data-testid="payment-over-limit">
                      Exceeds credit-adjusted remaining ({money(_creditAdjRemaining, currency)}).
                    </div>
                  )}
                </div>
                <div>
                  <Label>Date paid</Label>
                  <Input type="date" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} data-testid="payment-date-input" />
                </div>
              </div>
              <div>
                <Label>Payment method</Label>
                <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                  <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{PM_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Reference <span className="text-[#6F828A] font-normal">(optional)</span></Label>
                <Input value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} placeholder="Transaction ID, bank ref…" data-testid="payment-reference-input" />
              </div>
              <div>
                <Label>Note <span className="text-[#6F828A] font-normal">(optional)</span></Label>
                <Textarea value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} data-testid="payment-note-input" />
              </div>
            </div>
            );
          })()}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPayFor(null)} data-testid="payment-cancel-button">Cancel</Button>
            <Button
              onClick={submitPay}
              disabled={(() => {
                if (!payFor) return false;
                const allocs = payFor.credit_allocations || [];
                const allocSum = allocs.reduce((s, a) => s + (a.amount || 0), 0);
                const remaining = Math.max(0, (payFor.breakdown?.outstanding_to_payer || 0) - allocSum);
                const typed = parseFloat(payForm.amount) || 0;
                return typed <= 0 || typed > remaining + 0.005;
              })()}
              className="bg-[#1F9FA4] hover:bg-[#178085] disabled:opacity-50"
              data-testid="payment-save-button"
            >Record payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm receipt (payer's perspective) */}
      <Dialog open={!!confirmFor} onOpenChange={(v) => !v && setConfirmFor(null)}>
        <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3]" data-testid="confirm-dialog">
          <DialogHeader><DialogTitle className="font-heading">Confirm payment received</DialogTitle></DialogHeader>
          {confirmFor && (
            <div className="space-y-3">
              <p className="text-sm text-[#6F828A]">
                <span className="text-[#2E3A3F] font-medium">{confirmFor.payment.recorded_by_name || "Your co-parent"}</span> logged a payment to you for <span className="text-[#2E3A3F] font-medium">{confirmFor.exp.title}</span>. Tap Confirm once you&apos;ve actually received it, or Mark not received if you haven&apos;t — both actions are logged in the audit trail.
              </p>
              <div className="border border-[#D9E1E3] bg-[#EEF3F4] rounded-sm p-3 text-sm font-mono-data space-y-1">
                <div className="flex justify-between"><span>Amount</span><span className="font-semibold tabular-nums">{money(confirmFor.payment.amount, currency)}</span></div>
                <div className="flex justify-between"><span>Date paid</span><span>{confirmFor.payment.date}</span></div>
                <div className="flex justify-between"><span>Method</span><span>{PM_LABEL[confirmFor.payment.method] || confirmFor.payment.method}</span></div>
                {confirmFor.payment.reference && <div className="flex justify-between"><span>Reference</span><span className="truncate max-w-[60%] text-right">{confirmFor.payment.reference}</span></div>}
                {confirmFor.payment.note && <div className="text-xs text-[#6F828A] italic pt-1 border-t border-[#D9E1E3] mt-1">{confirmFor.payment.note}</div>}
              </div>
              <div>
                <Label className="text-xs text-[#6F828A]">If disputing, please explain why</Label>
                <Textarea value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)} placeholder="e.g. No matching transfer in my account; wrong amount; wrong reference…" data-testid="dispute-reason-input" />
                {/* v77.99.55 — Voice-to-text drop-in. Same Web Speech
                    component as Journal / Messages / Requests. Smart
                    append so a typed reason is preserved across
                    recording sessions; never auto-submits. */}
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                  <VoiceDictateButton
                    testidPrefix="dispute-reason-voice"
                    onTranscript={(t) =>
                      setDisputeReason((prev) => (prev ? prev + (prev.endsWith(" ") ? "" : " ") : "") + t)
                    }
                    disabled={confirmBusy}
                  />
                  <span className="text-[11px] text-[#6F828A] leading-snug">
                    Use voice-to-text to draft your reason. You can review and edit before submitting.
                  </span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="ghost" onClick={() => setConfirmFor(null)} disabled={confirmBusy} data-testid="confirm-cancel-button">Close</Button>
            <Button variant="outline" onClick={doDispute} disabled={confirmBusy} className="border-[#C0291D] text-[#C0291D] hover:bg-[#FBEFED] rounded-sm" data-testid="dispute-button">
              <Ban className="w-4 h-4 mr-1" /> Mark not received
            </Button>
            <Button onClick={doConfirm} disabled={confirmBusy} className="bg-[#1F9FA4] hover:bg-[#178085]" data-testid="confirm-button">
              <Check className="w-4 h-4 mr-1" /> Confirm receipt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record payment received (payer's perspective — single-sided, auto-confirmed) */}
      <Dialog open={!!receiptFor} onOpenChange={(v) => !v && setReceiptFor(null)}>
        <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3]" data-testid="receipt-dialog">
          <DialogHeader><DialogTitle className="font-heading">Record payment received</DialogTitle></DialogHeader>
          {receiptFor && (() => {
            // v77.99.114 — Mirror the credit-adjusted-remaining logic
            // from the Record Payment dialog (and backend safeguard).
            const _allocs = receiptFor.credit_allocations || [];
            const _allocSum = _allocs.reduce((s, a) => s + (a.amount || 0), 0);
            const _grossRemaining = receiptFor.breakdown?.outstanding_to_payer || 0;
            const _creditAdjRemaining = Math.max(0, _grossRemaining - _allocSum);
            const _typed = parseFloat(receiptForm.amount) || 0;
            const _overLimit = _typed > _creditAdjRemaining + 0.005;
            return (
            <div className="space-y-3">
              <p className="text-sm text-[#6F828A]">
                Record a payment you&apos;ve received toward <span className="text-[#2E3A3F] font-medium">{receiptFor.title}</span>. This is auto-confirmed and will show up in the payment log as <span className="text-[#2E3A3F] font-medium">&quot;Recorded by recipient&quot;</span> — no confirmation needed from your co-parent. Remaining: <span className="font-mono-data text-[#C0291D] font-semibold">{money(_creditAdjRemaining, currency)}</span>
              </p>
              {_allocSum > 0.005 && (
                <div className="text-xs text-[#0F5658] bg-[#D6F0EF] border border-[#A7D8DA] rounded-sm p-2" data-testid="receipt-credit-note">
                  <strong className="font-mono-data">{money(_allocSum, currency)}</strong> is already covered by a payment credit on this expense (gross {money(_grossRemaining, currency)}). Recording more than {money(_creditAdjRemaining, currency)} would double-credit the same money.
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Amount received ({currencySymbol(currency)})</Label>
                  <Input
                    type="number"
                    step="0.01"
                    max={_creditAdjRemaining}
                    value={receiptForm.amount}
                    onChange={(e) => setReceiptForm({ ...receiptForm, amount: e.target.value })}
                    className={_overLimit ? "border-[#A93A2F] focus-visible:ring-[#A93A2F]" : ""}
                    data-testid="receipt-amount-input"
                  />
                  {_overLimit && (
                    <div className="text-[11px] text-[#A93A2F] mt-1" data-testid="receipt-over-limit">
                      Exceeds credit-adjusted remaining ({money(_creditAdjRemaining, currency)}).
                    </div>
                  )}
                </div>
                <div>
                  <Label>Date received</Label>
                  <Input type="date" value={receiptForm.date} onChange={(e) => setReceiptForm({ ...receiptForm, date: e.target.value })} data-testid="receipt-date-input" />
                </div>
              </div>
              <div>
                <Label>Payment method</Label>
                <Select value={receiptForm.method} onValueChange={(v) => setReceiptForm({ ...receiptForm, method: v })}>
                  <SelectTrigger data-testid="receipt-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{PM_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Reference <span className="text-[#6F828A] font-normal">(optional)</span></Label>
                <Input value={receiptForm.reference} onChange={(e) => setReceiptForm({ ...receiptForm, reference: e.target.value })} placeholder="Transaction ID, bank ref, or 'Other' detail…" data-testid="receipt-reference-input" />
              </div>
              <div>
                <Label>Note <span className="text-[#6F828A] font-normal">(optional)</span></Label>
                <Textarea value={receiptForm.note} onChange={(e) => setReceiptForm({ ...receiptForm, note: e.target.value })} data-testid="receipt-note-input" />
              </div>
            </div>
            );
          })()}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReceiptFor(null)} disabled={receiptBusy} data-testid="receipt-cancel-button">Cancel</Button>
            <Button
              onClick={submitReceipt}
              disabled={(() => {
                if (!receiptFor || receiptBusy) return true;
                const allocs = receiptFor.credit_allocations || [];
                const allocSum = allocs.reduce((s, a) => s + (a.amount || 0), 0);
                const remaining = Math.max(0, (receiptFor.breakdown?.outstanding_to_payer || 0) - allocSum);
                const typed = parseFloat(receiptForm.amount) || 0;
                return typed <= 0 || typed > remaining + 0.005;
              })()}
              className="bg-[#1F9FA4] hover:bg-[#178085] disabled:opacity-50"
              data-testid="receipt-save-button"
            >{receiptBusy ? "Recording…" : "Record receipt"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v77 — Attach receipt dialog */}
      <Dialog open={!!attachFor} onOpenChange={(v) => !v && setAttachFor(null)}>
        <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3]" data-testid="attach-receipt-dialog">
          <DialogHeader><DialogTitle className="font-heading">Add a receipt</DialogTitle></DialogHeader>
          {attachFor && (
            <form onSubmit={submitAttach} className="space-y-3 text-sm">
              <p className="text-[#6F828A]">
                Attach a receipt or invoice to <span className="text-[#2E3A3F] font-medium">{attachFor.title}</span>.
                Images and PDFs are supported. You can attach more than one — they&apos;ll all show up on the expense card and in the PDF export.
              </p>
              <div>
                <Label className="flex items-center gap-2"><Paperclip className="w-3.5 h-3.5" /> Receipt file</Label>
                <Input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setAttachFile(e.target.files?.[0] || null)}
                  data-testid="attach-receipt-input"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setAttachFor(null)} disabled={attachBusy} data-testid="attach-receipt-cancel-button">Cancel</Button>
                <Button type="submit" disabled={!attachFile || attachBusy} className="bg-[#1F9FA4] hover:bg-[#178085]" data-testid="attach-receipt-save-button">
                  {attachBusy ? "Uploading…" : "Attach receipt"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit */}
      <Dialog open={!!editFor} onOpenChange={(v) => !v && (setEditFor(null), setEditForm(null))}>
        <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3] max-h-[90vh] overflow-y-auto" data-testid="edit-dialog">
          <DialogHeader><DialogTitle className="font-heading">Edit expense</DialogTitle></DialogHeader>
          {editFor && editForm && (
            <div className="space-y-3">
              {(editFor.payments || []).length > 0 && (
                <div className="border border-[#E8C29A] bg-[#FBF0DC] text-[#5C3A1E] rounded-sm p-3 text-xs" data-testid="edit-warning">
                  This expense has reimbursement payments logged. Financial changes will require confirmation.
                </div>
              )}
              <div><Label>Title</Label><Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} data-testid="edit-title-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <Select value={editForm.category} onValueChange={(v) => setEditForm({ ...editForm, category: v })}>
                    <SelectTrigger data-testid="edit-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Amount ({currencySymbol(currency)})</Label><Input type="number" step="0.01" value={editForm.amount} onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })} data-testid="edit-amount-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date</Label><Input type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} data-testid="edit-date-input" /></div>
                <div>
                  <Label>Paid upfront by</Label>
                  <Select value={editForm.paid_by} onValueChange={(v) => setEditForm({ ...editForm, paid_by: v })}>
                    <SelectTrigger data-testid="edit-paidby-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {eligibleParentsFor(editForm.child_ids || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Split</Label>
                <Select value={editForm.split_type} onValueChange={(v) => setEditForm({ ...editForm, split_type: v })}>
                  <SelectTrigger data-testid="edit-split-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{SPLIT_OPTIONS_NON_DEFAULT.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {editForm.split_type === "custom" && (
                <div>
                  <Label>Payer share (%)</Label>
                  <Input type="number" min={0} max={100} value={editForm.split_payer_share} onChange={(e) => setEditForm({ ...editForm, split_payer_share: e.target.value })} data-testid="edit-payer-share-input" />
                </div>
              )}
              <ChildAssignPicker
                childList={childList}
                allChildren={editForm.all_children}
                childIds={editForm.child_ids}
                onChange={(patch) => setEditForm({ ...editForm, ...patch })}
                testPrefix="edit"
              />
              <div><Label>Notes</Label><Textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} data-testid="edit-notes-input" /></div>
              {/* Receipts panel */}
              <div className="border border-[#D9E1E3] bg-[#FAFBFB] rounded-sm p-3" data-testid="edit-receipts-panel">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <Label className="flex items-center gap-2 mb-0"><Paperclip className="w-3.5 h-3.5" /> Receipts</Label>
                  <div className="flex items-center gap-3">
                    {(() => {
                      const liveExp = items.find((it) => it.id === editFor.id) || editFor;
                      const r = deriveReceiptStatus(liveExp);
                      return (
                        <>
                          {r.status === "missing" && (
                            <button
                              type="button"
                              onClick={() => setNotRequired(liveExp, true)}
                              className="inline-flex items-center gap-1 text-[11px] text-[#2E5044] hover:underline"
                              data-testid="edit-receipt-mark-not-needed-button"
                            >
                              <CheckCircle2 className="w-3 h-3" /> Mark as not needed
                            </button>
                          )}
                          {r.status === "not_required" && (
                            <button
                              type="button"
                              onClick={() => setNotRequired(liveExp, false)}
                              className="inline-flex items-center gap-1 text-[11px] text-[#7A5A1E] hover:underline"
                              data-testid="edit-receipt-undo-not-needed-button"
                            >Undo &quot;not needed&quot;</button>
                          )}
                          <button
                            type="button"
                            onClick={() => openAttach(liveExp)}
                            className="inline-flex items-center gap-1 text-[11px] text-[#1F9FA4] hover:underline"
                            data-testid="edit-receipt-add-button"
                          >
                            <Plus className="w-3 h-3" /> Add receipt
                          </button>
                        </>
                      );
                    })()}
                  </div>
                </div>
                {(() => {
                  const liveExp = items.find((it) => it.id === editFor.id) || editFor;
                  const r = deriveReceiptStatus(liveExp);
                  if (r.status === "not_required") {
                    return (
                      <div className="mt-1.5 inline-flex items-center gap-1 text-xs text-[#2E5044]" data-testid="edit-receipts-not-required-hint">
                        <CheckCircle2 className="w-3 h-3" /> Marked as not needing a receipt.
                      </div>
                    );
                  }
                  if (r.ids.length === 0) {
                    return <div className="mt-1.5 text-xs text-[#6F828A]">No receipts attached. Click &quot;Add receipt&quot; to upload one or &quot;Mark as not needed&quot; if there isn&apos;t one to attach.</div>;
                  }
                  return (
                    <ul className="mt-2 space-y-1" data-testid="edit-receipts-list">
                      {r.ids.map((rid) => (
                        <li key={rid} className="flex items-center gap-2 text-xs">
                          <a
                            href={`${API}/files/${rid}/download`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-[#1F9FA4] hover:underline truncate"
                          >
                            <Paperclip className="w-3 h-3 shrink-0" />
                            <span className="truncate">Receipt {rid.slice(0, 8)}</span>
                          </a>
                          <button
                            type="button"
                            onClick={() => detachReceipt(liveExp, rid)}
                            className="ml-auto text-[#C0291D] hover:text-[#8C1F15]"
                            title="Remove receipt"
                            data-testid={`edit-receipt-remove-${rid}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  );
                })()}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setEditFor(null); setEditForm(null); }} disabled={editBusy} data-testid="edit-cancel-button">Cancel</Button>
            <Button onClick={() => submitEdit(false)} disabled={editBusy} className="bg-[#1F9FA4] hover:bg-[#178085]" data-testid="edit-save-button">{editBusy ? "Saving…" : "Save changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void */}
      <Dialog open={!!voidFor} onOpenChange={(v) => !v && setVoidFor(null)}>
        <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3]" data-testid="void-dialog">
          <DialogHeader><DialogTitle className="font-heading">Void this expense?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#6F828A]">The entry stays in the ledger and audit trail, clearly marked as voided, but is excluded from active balances and totals. You can restore it later if needed.</p>
          <div>
            <Label>Reason</Label>
            <Textarea value={voidReason} onChange={(e) => setVoidReason(e.target.value)} placeholder="e.g. Entered twice by mistake; actual amount was different; etc." data-testid="void-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setVoidFor(null)} data-testid="void-cancel-button">Cancel</Button>
            <Button onClick={submitVoid} className="bg-[#C0291D] hover:bg-[#A8251A] text-white" data-testid="void-confirm-button">Void expense</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ExpenseDialogs;
